import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Navbar from "../components/Navbar";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Separator } from "../components/ui/separator";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { toast } from "sonner";
import { CreditCard, Truck, Check, FileText, Wallet, Calendar, Clock, MapPin } from "lucide-react";
import { Badge } from "../components/ui/badge";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

export default function ServiceCheckoutPage() {
    const navigate = useNavigate();
    const location = useLocation();
    const { user, isAuthenticated, token } = useAuth();
    const [loading, setLoading] = useState(false);
    const [step, setStep] = useState(1);

    // Get booking data from location state (passed from ServiceProductView)
    const { product, booking, totalPrice } = location.state || {};

    // Redirect if no data
    useEffect(() => {
        if (!product || !booking) {
            toast.error("Données de réservation manquantes");
            navigate("/");
        }
    }, [product, booking, navigate]);

    // Billing Address State
    const [billingAddress, setBillingAddress] = useState({
        first_name: "", last_name: "", address: "", city: "", postal_code: "", country: "Haïti", phone: ""
    });
    const [useShippingAsBilling, setUseShippingAsBilling] = useState(true);

    // Payment State
    const [paymentMethod, setPaymentMethod] = useState("card");

    useEffect(() => {
        if (user) {
            setBillingAddress(prev => ({
                ...prev,
                first_name: user.first_name || "",
                last_name: user.last_name || "",
                phone: user.phone || ""
            }));
        }
    }, [user]);

    const handleBillingChange = (e) => {
        setBillingAddress({ ...billingAddress, [e.target.name]: e.target.value });
    };

    const handleSubmitOrder = async () => {
        if (!isAuthenticated || !token) {
            toast.error("Veuillez vous connecter pour commander");
            return;
        }

        setLoading(true);
        try {
            // Prepare order data
            const orderData = {
                items: [{
                    product_id: product.id,
                    quantity: 1,
                    booking_date: booking.booking_start,
                    booking_end: booking.booking_end,
                    service_location: booking.serviceLocation,
                    travel_fee: booking.travelFee || 0
                }],
                shipping_address: booking.deliveryAddress || {},
                billing_address: useShippingAsBilling ? (booking.deliveryAddress || {}) : billingAddress,
                payment_method: paymentMethod,
                is_service_order: true // Flag for backend
            };

            await axios.post(`${API_URL}/orders`, orderData, {
                headers: { Authorization: `Bearer ${token}` }
            });

            toast.success("Réservation confirmée !");
            navigate(`/commandes`);
        } catch (error) {
            let message = "Erreur lors de la réservation";
            if (error.response?.data?.detail) {
                const detail = error.response.data.detail;
                message = typeof detail === 'string' ? detail : JSON.stringify(detail);
            }
            toast.error(message);
        } finally {
            setLoading(false);
        }
    };

    if (!product || !booking) return null;

    return (
        <div className="min-h-screen bg-background">
            <Navbar />

            <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-5xl py-8">
                <h1 className="font-heading font-bold text-3xl md:text-4xl text-foreground mb-8">
                    Finaliser votre réservation
                </h1>

                {/* Progress Steps */}
                <div className="flex items-center justify-center mb-12">
                    <div className="flex items-center">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${step >= 1 ? "bg-orange-500 text-white" : "bg-muted text-muted-foreground"}`}>
                            {step > 1 ? <Check className="h-5 w-5" /> : "1"}
                        </div>
                        <span className="ml-2 font-medium">Informations</span>
                    </div>
                    <div className="w-16 md:w-32 h-0.5 bg-muted mx-4"></div>
                    <div className="flex items-center">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${step >= 2 ? "bg-orange-500 text-white" : "bg-muted text-muted-foreground"}`}>
                            {step > 2 ? <Check className="h-5 w-5" /> : "2"}
                        </div>
                        <span className="ml-2 font-medium">Paiement</span>
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    <div className="lg:col-span-2">
                        {step === 1 && (
                            <div className="space-y-6">
                                {/* Booking Summary Card */}
                                <Card className="border-orange-100 bg-orange-50/30">
                                    <CardHeader>
                                        <CardTitle className="text-lg flex items-center gap-2 text-orange-700">
                                            <Calendar className="h-5 w-5" /> Détails de la séance
                                        </CardTitle>
                                    </CardHeader>
                                    <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div className="flex items-center gap-3">
                                            <div className="p-2 bg-white rounded-lg shadow-sm">
                                                <Calendar className="h-5 w-5 text-orange-500" />
                                            </div>
                                            <div>
                                                <p className="text-xs text-gray-500 uppercase font-bold">Date</p>
                                                <p className="font-medium">{new Date(booking.booking_start).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}</p>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-3">
                                            <div className="p-2 bg-white rounded-lg shadow-sm">
                                                <Clock className="h-5 w-5 text-orange-500" />
                                            </div>
                                            <div>
                                                <p className="text-xs text-gray-500 uppercase font-bold">Heure</p>
                                                <p className="font-medium">{booking.bookingTime}</p>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-3 md:col-span-2">
                                            <div className="p-2 bg-white rounded-lg shadow-sm">
                                                <MapPin className="h-5 w-5 text-orange-500" />
                                            </div>
                                            <div>
                                                <p className="text-xs text-gray-500 uppercase font-bold">Lieu</p>
                                                <p className="font-medium">
                                                    {booking.serviceLocation === 'boutique' ? "À la boutique de l'artisan" : `À domicile: ${booking.deliveryAddress?.address}, ${booking.deliveryAddress?.city}`}
                                                </p>
                                            </div>
                                        </div>
                                    </CardContent>
                                </Card>

                                {/* Billing Address Form */}
                                <Card>
                                    <CardHeader>
                                        <CardTitle className="flex items-center gap-2">
                                            <FileText className="h-5 w-5" />
                                            Adresse de facturation
                                        </CardTitle>
                                    </CardHeader>
                                    <CardContent className="space-y-4">
                                        <div className="flex items-center space-x-2 border p-3 rounded-lg bg-gray-50">
                                            <input
                                                type="checkbox"
                                                id="same_address"
                                                checked={useShippingAsBilling}
                                                onChange={(e) => setUseShippingAsBilling(e.target.checked)}
                                                className="rounded border-gray-300"
                                            />
                                            <Label htmlFor="same_address" className="cursor-pointer font-normal">Identique à l'adresse de service/profil</Label>
                                        </div>

                                        {!useShippingAsBilling && (
                                            <div className="grid grid-cols-1 gap-4 animate-in fade-in slide-in-from-top-2">
                                                <div className="grid grid-cols-2 gap-4">
                                                    <div className="space-y-2">
                                                        <Label>Prénom</Label>
                                                        <Input name="first_name" value={billingAddress.first_name} onChange={handleBillingChange} />
                                                    </div>
                                                    <div className="space-y-2">
                                                        <Label>Nom</Label>
                                                        <Input name="last_name" value={billingAddress.last_name} onChange={handleBillingChange} />
                                                    </div>
                                                </div>
                                                <div className="space-y-2">
                                                    <Label>Adresse</Label>
                                                    <Input name="address" value={billingAddress.address} onChange={handleBillingChange} />
                                                </div>
                                                <div className="grid grid-cols-2 gap-4">
                                                    <div className="space-y-2">
                                                        <Label>Ville</Label>
                                                        <Input name="city" value={billingAddress.city} onChange={handleBillingChange} />
                                                    </div>
                                                    <div className="space-y-2">
                                                        <Label>Téléphone</Label>
                                                        <Input name="phone" value={billingAddress.phone} onChange={handleBillingChange} />
                                                    </div>
                                                </div>
                                            </div>
                                        )}

                                        <Button className="w-full mt-4 bg-orange-600 hover:bg-orange-700" onClick={() => setStep(2)}>
                                            Continuer vers le paiement
                                        </Button>
                                    </CardContent>
                                </Card>
                            </div>
                        )}

                        {step === 2 && (
                            <Card>
                                <CardHeader>
                                    <CardTitle className="flex items-center gap-2">
                                        <CreditCard className="h-5 w-5" />
                                        Paiement sécurisé
                                    </CardTitle>
                                </CardHeader>
                                <CardContent className="space-y-6">
                                    <RadioGroup value={paymentMethod} onValueChange={setPaymentMethod}>
                                        <div className="flex items-center space-x-2 p-4 border rounded-lg hover:bg-gray-50 cursor-pointer">
                                            <RadioGroupItem value="card" id="card" />
                                            <Label htmlFor="card" className="flex-1 cursor-pointer">
                                                <div className="flex items-center gap-2 font-medium">
                                                    <CreditCard className="h-4 w-4" /> Carte Bancaire
                                                </div>
                                            </Label>
                                        </div>
                                        <div className="flex items-center space-x-2 p-4 border rounded-lg hover:bg-gray-50 cursor-pointer">
                                            <RadioGroupItem value="moncash" id="moncash" />
                                            <Label htmlFor="moncash" className="flex-1 cursor-pointer">
                                                <div className="flex items-center gap-2 font-medium">
                                                    📱 MonCash
                                                </div>
                                            </Label>
                                        </div>
                                        <div className="flex items-center space-x-2 p-4 border rounded-lg hover:bg-gray-50 cursor-pointer">
                                            <RadioGroupItem value="wallet" id="wallet" disabled={user?.wallet_balance < totalPrice} />
                                            <Label htmlFor="wallet" className={`flex-1 cursor-pointer ${user?.wallet_balance < totalPrice ? 'opacity-50' : ''}`}>
                                                <div className="flex items-center gap-2 font-medium">
                                                    <Wallet className="h-4 w-4" /> Portefeuille BuyMore
                                                </div>
                                                <p className="text-xs text-gray-500 mt-1">
                                                    Solde disponible: <strong>{user?.wallet_balance?.toFixed(2) || 0} HTG</strong>
                                                    {user?.wallet_balance < totalPrice && <span className="text-red-500 ml-2">(Insuffisant)</span>}
                                                </p>
                                            </Label>
                                        </div>
                                    </RadioGroup>

                                    <div className="flex gap-4">
                                        <Button variant="outline" onClick={() => setStep(1)} className="flex-1">Retour</Button>
                                        <Button className="flex-1 bg-green-600 hover:bg-green-700" onClick={handleSubmitOrder} disabled={loading}>
                                            {loading ? "Traitement..." : `Confirmer & Payer ${totalPrice.toFixed(2)} HTG`}
                                        </Button>
                                    </div>
                                </CardContent>
                            </Card>
                        )}
                    </div>

                    {/* Summary Sidebar */}
                    <div className="lg:col-span-1">
                        <Card className="sticky top-24 border-purple-100">
                            <CardHeader>
                                <CardTitle>Récapitulatif de service</CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div className="flex gap-3">
                                    <div className="w-16 h-16 rounded bg-gray-50 border overflow-hidden">
                                        <img src={product.images?.[0]} className="w-full h-full object-cover" alt={product.title} />
                                    </div>
                                    <div className="flex-1">
                                        <p className="font-bold text-gray-900">{product.title}</p>
                                        <Badge variant="outline" className="mt-1">Service</Badge>
                                    </div>
                                </div>

                                <Separator />

                                <div className="space-y-2 text-sm">
                                    <div className="flex justify-between">
                                        <span className="text-gray-500">Service</span>
                                        <span className="font-medium">{product.discount_price ? product.discount_price.toFixed(2) : product.price.toFixed(2)} HTG</span>
                                    </div>
                                    {booking.travelFee > 0 && (
                                        <div className="flex justify-between">
                                            <span className="text-gray-500">Frais de déplacement</span>
                                            <span className="font-medium">{booking.travelFee.toFixed(2)} HTG</span>
                                        </div>
                                    )}
                                </div>

                                <Separator />

                                <div className="flex justify-between font-bold text-xl text-orange-600">
                                    <span>Total</span>
                                    <span>{totalPrice.toFixed(2)} HTG</span>
                                </div>
                                <p className="text-[10px] text-gray-400 text-center italic">
                                    En confirmant, vous acceptez nos conditions de réservation et d'annulation.
                                </p>
                            </CardContent>
                        </Card>
                    </div>
                </div>
            </div>
        </div>
    );
}
